/**
 * Progressive disclosure pipeline — ARCHITECTURE.md section 5.2.
 *
 * "The analyst UI shows the investigation progressing in real time.
 * As each collection task completes and entities are extracted,
 * nodes appear on the graph incrementally."
 *
 * This module orchestrates the collect → extract → validate cycle
 * so that entities appear as soon as their source data is available,
 * rather than waiting for all collection to finish.
 *
 * Flow per task:
 * 1. Collection executor fetches raw data
 * 2. Relevance filter scores the text
 * 3. Entity extractor pulls structured entities
 * 4. Validation checks schema, completeness, plausibility, grounding
 * 5. Valid entities → SQLite + event bus → UI sees node appear
 * 6. Resource manager updated after each extraction
 *
 * The pipeline respects resource limits at every step — if the budget
 * is exhausted mid-collection, it transitions to review with partial results.
 */

import { eventBus } from '@/core/events';
import { getDeskConfig } from '@/config/desk-config';
import type { DeskConfig } from '@/config/desk-config';
import type { LLMAdapter } from '@/intelligence/ai/llm-adapter';
import { TokenTracker, createLLMAdapter } from '@/intelligence/ai/llm-adapter';
import { createRelevanceFilter } from '@/intelligence/ai/relevance-filter';
import type { RelevanceFilter } from '@/intelligence/ai/relevance-filter';
import { createEntityExtractor } from '@/intelligence/ai/entity-extractor';
import type { EntityExtractor } from '@/intelligence/ai/entity-extractor';
import { entityStore } from '@/intelligence/entities/entity-store';
import type { Entity, EntityType } from '@/intelligence/entities/types';
import type {
  CollectionTask,
  ScopeContract,
} from './types';
import { INVESTIGATION_TIERS } from './types';
import {
  getInvestigation,
  updateInvestigation,
  addLogEntry,
  linkEntityToInvestigation,
  getInvestigationEntityIds,
} from './investigation-store';
import { createResourceManager } from './resource-manager';
import type { ResourceManager } from './resource-manager';
import { executeCollectionTasks } from './collection-executor';
import { prefilterByKeywords } from './keyword-prefilter';
import { extractIOCs } from './ioc-extractor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProgressiveResult {
  investigationId: string;
  tasksCompleted: number;
  tasksFailed: number;
  entitiesExtracted: number;
  entitiesRejected: number;
  tokensUsed: number;
  resourceLimitHit: boolean;
  reason?: string;
}

export interface ProgressiveConfig {
  /** Max concurrent collection tasks (default 3). */
  concurrency?: number;
  /** Per-task timeout in ms (default 30000). */
  taskTimeoutMs?: number;
}

// ---------------------------------------------------------------------------
// Default extraction entity types
// ---------------------------------------------------------------------------

/**
 * Build the set of entity types to extract based on scope contract.
 */
function buildEntityTypes(scope: ScopeContract): EntityType[] {
  const types = new Set<EntityType>();

  // Add subject type
  if (scope.subject.type) {
    types.add(scope.subject.type as EntityType);
  }

  // Always include common types
  types.add('LOCATION');
  types.add('SOURCE');
  types.add('ORGANIZATION');
  types.add('PERSON');
  types.add('EVENT');
  types.add('ASSET');
  types.add('INDICATOR');

  return Array.from(types);
}

// ---------------------------------------------------------------------------
// Progressive pipeline
// ---------------------------------------------------------------------------

/**
 * Run the progressive collection → extraction pipeline.
 *
 * True progressive disclosure: as each collection task completes,
 * its data flows through the extraction pipeline immediately.
 * Entities appear on the graph incrementally, not in a batch.
 *
 * Flow per task:
 * task completes → relevance filter → entity extraction → store + emit
 *
 * This function is called by the orchestrator during the COLLECT state.
 * It handles both collection and extraction, then the orchestrator
 * transitions through the remaining states.
 */
export async function runProgressivePipeline(
  investigationId: string,
  config?: ProgressiveConfig,
): Promise<ProgressiveResult> {
  const investigation = await getInvestigation(investigationId);
  if (!investigation) {
    throw new Error(`Investigation not found: ${investigationId}`);
  }
  if (!investigation.scopeContract) {
    throw new Error(`No scope contract for investigation: ${investigationId}`);
  }

  const deskConfig = getDeskConfig(investigation.deskId);
  if (!deskConfig) {
    throw new Error(`Desk config not found: ${investigation.deskId}`);
  }

  const tierConfig = INVESTIGATION_TIERS[investigation.tier];
  const tokenTracker = new TokenTracker();
  const resourceManager = createResourceManager(
    investigationId,
    tierConfig,
    new Date(investigation.startedAt),
  );
  const adapter = createLLMAdapter(tokenTracker);

  // Pipeline components
  const relevanceFilter = createRelevanceFilter(adapter, deskConfig.priorityIndicators);
  const extractor = createEntityExtractor(adapter);
  const entityTypes = buildEntityTypes(investigation.scopeContract);

  const result: ProgressiveResult = {
    investigationId,
    tasksCompleted: 0,
    tasksFailed: 0,
    entitiesExtracted: 0,
    entitiesRejected: 0,
    tokensUsed: 0,
    resourceLimitHit: false,
  };

  // Progressive disclosure: listen for task completion events and
  // process each task's data through extraction as it arrives.
  // This ensures entities appear incrementally, not in a batch.
  const scope = investigation.scopeContract;
  const pendingExtractions: Promise<void>[] = [];

  const unsubscribe = eventBus.on('investigation:task-completed', (event) => {
    if (event.investigationId !== investigationId) return;

    const task = event.task;
    if (task.status !== 'completed' || !task.rawData) {
      result.tasksFailed++;
      return;
    }

    result.tasksCompleted++;

    // Check resource limits before starting extraction
    const resourceCheck = resourceManager.check();
    if (!resourceCheck.canContinue && resourceCheck.recommendation === 'hard_stop') {
      result.resourceLimitHit = true;
      result.reason = resourceCheck.reason;
      return;
    }

    // Process this task's data through extraction pipeline immediately
    const extractionPromise = processTaskData(
      task,
      investigationId,
      scope,
      deskConfig,
      entityTypes,
      relevanceFilter,
      extractor,
      adapter,
      tokenTracker,
      resourceManager,
    ).then((extractionResult) => {
      result.entitiesExtracted += extractionResult.extracted;
      result.entitiesRejected += extractionResult.rejected;
    }).catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[Sentinel] Progressive extraction error: ${message}`);
    });

    pendingExtractions.push(extractionPromise);
  });

  try {
    // Execute collection tasks — the event listener above will
    // process each task's data as it completes
    await executeCollectionTasks(investigationId, {
      concurrency: config?.concurrency ?? 3,
      taskTimeoutMs: config?.taskTimeoutMs ?? 30_000,
    });

    // Wait for any in-flight extractions to finish
    await Promise.all(pendingExtractions);
  } finally {
    unsubscribe();
  }

  // Update investigation with final counts
  const entityIds = await getInvestigationEntityIds(investigationId);
  result.tokensUsed = tokenTracker.totalTokens;

  await updateInvestigation(investigationId, {
    tokenUsage: tokenTracker.totalTokens,
    nodeCount: entityIds.length,
  });

  resourceManager.updateGraphCounts(entityIds.length, 0);

  return result;
}

// ---------------------------------------------------------------------------
// Per-task extraction
// ---------------------------------------------------------------------------

interface TaskExtractionResult {
  extracted: number;
  rejected: number;
}

/**
 * Process raw data from a single collection task through the extraction pipeline.
 *
 * Flow: raw text → relevance filter → entity extractor → store + emit
 *
 * Entities appear in the UI as soon as they are extracted and validated.
 */
async function processTaskData(
  task: CollectionTask,
  investigationId: string,
  _scopeContract: ScopeContract,
  deskConfig: DeskConfig,
  entityTypes: EntityType[],
  relevanceFilter: RelevanceFilter,
  extractor: EntityExtractor,
  _adapter: LLMAdapter,
  tokenTracker: TokenTracker,
  resourceManager: ResourceManager,
): Promise<TaskExtractionResult> {
  const result: TaskExtractionResult = { extracted: 0, rejected: 0 };
  const rawText = task.rawData!;

  try {
    // Step 0: Keyword prefilter — zero-cost rule-based gate before LLM
    const scopeKeywords = [
      _scopeContract.subject.description,
      ..._scopeContract.subject.watchlistMatches,
    ].filter(Boolean);
    const watchlistKeywords = deskConfig.entityWatchlists.map(w => w.name);
    const prefilter = prefilterByKeywords(rawText, scopeKeywords, watchlistKeywords);

    if (!prefilter.passed) {
      await addLogEntry({
        investigationId,
        state: 'extract',
        action: 'entity_rejected',
        detail: {
          taskId: task.id,
          reason: 'keyword_prefilter_failed',
          sourceId: task.sourceId,
        },
      });
      return result;
    }

    // Step 1: Relevance filter (Haiku — fast, cheap gate)
    const tokensBeforeFilter = tokenTracker.totalTokens;
    const filterResult = await relevanceFilter.filter(rawText);
    const filterTokens = tokenTracker.totalTokens - tokensBeforeFilter;
    resourceManager.recordTokens(filterTokens);

    await addLogEntry({
      investigationId,
      state: 'extract',
      action: 'llm_call',
      detail: {
        purpose: 'relevance_filter',
        taskId: task.id,
        sourceId: task.sourceId,
        score: filterResult.relevance.score,
        passed: filterResult.passed,
        tokensUsed: filterTokens,
      },
    });

    if (!filterResult.passed) {
      await addLogEntry({
        investigationId,
        state: 'extract',
        action: 'entity_rejected',
        detail: {
          taskId: task.id,
          reason: 'relevance_below_threshold',
          score: filterResult.relevance.score,
        },
      });
      resourceManager.recordInfoGain(0);
      return result;
    }

    // Step 1.5: IOC extraction (regex, zero-cost, 100% precision)
    const iocs = extractIOCs(rawText);
    if (iocs.length > 0) {
      const sourceUrl = String((task.query as Record<string, unknown>).url
        ?? (task.query as Record<string, unknown>).endpoint
        ?? '');
      const collectorId = `collector_${task.sourceId}`;
      const collectedAt = task.completedAt ?? new Date().toISOString();

      for (const ioc of iocs) {
        const iocEntity: Omit<Entity, 'id'> = {
          type: 'INDICATOR',
          name: ioc.value,
          aliases: [],
          description: `${ioc.type} indicator extracted by regex`,
          properties: { iocType: ioc.type, offset: ioc.offset },
          provenance: {
            sourceUrl,
            extractionQuote: ioc.value,
            collectorId,
            collectedAt,
            confidence: 1.0,
          },
          corroboration: {
            count: 1,
            sources: [sourceUrl],
            natoRating: 'F6',
          },
          sources: [],
          relationships: [],
          firstSeen: collectedAt,
          lastSeen: collectedAt,
          confidence: 1.0,
          tags: ['ioc', ioc.type.toLowerCase()],
          notes: [],
          watchStatus: 'none',
        };

        const savedEntity = await entityStore.create(iocEntity);
        await linkEntityToInvestigation(investigationId, savedEntity.id);
        eventBus.emit('investigation:entity-extracted', { investigationId, entity: savedEntity });
        result.extracted++;
      }

      await addLogEntry({
        investigationId,
        state: 'extract',
        action: 'entity_extracted',
        detail: {
          taskId: task.id,
          method: 'regex_ioc',
          count: iocs.length,
          types: [...new Set(iocs.map(i => i.type))],
        },
      });
    }

    // Step 2: Entity extraction (Sonnet)
    const tokensBeforeExtract = tokenTracker.totalTokens;
    const extractionResult = await extractor.extract(
      rawText,
      {
        sourceUrl: String((task.query as Record<string, unknown>).url
          ?? (task.query as Record<string, unknown>).endpoint
          ?? ''),
        collectorId: `collector_${task.sourceId}`,
        collectedAt: task.completedAt ?? new Date().toISOString(),
        sourceId: task.sourceId,
      },
      entityTypes,
      deskConfig.ontologyExtensions,
    );
    const extractTokens = tokenTracker.totalTokens - tokensBeforeExtract;
    resourceManager.recordTokens(extractTokens);

    await addLogEntry({
      investigationId,
      state: 'extract',
      action: 'llm_call',
      detail: {
        purpose: 'entity_extraction',
        taskId: task.id,
        sourceId: task.sourceId,
        extracted: extractionResult.extracted.length,
        rejected: extractionResult.rejected.length,
        tokensUsed: extractTokens,
        model: extractionResult.model,
      },
    });

    // Step 3: Store valid entities and emit events (progressive disclosure)
    for (const extracted of extractionResult.extracted) {
      const entityData: Omit<Entity, 'id'> = {
        type: extracted.type,
        name: extracted.name,
        aliases: extracted.aliases ?? [],
        description: extracted.description ?? '',
        properties: extracted.properties ?? {},
        provenance: extracted.provenance,
        corroboration: {
          count: 1,
          sources: [extracted.provenance.sourceUrl],
          natoRating: 'F6', // Default until corroboration pass (S11)
        },
        sources: [],
        relationships: [],
        firstSeen: extracted.provenance.collectedAt,
        lastSeen: extracted.provenance.collectedAt,
        confidence: extracted.provenance.confidence,
        tags: [],
        notes: [],
        watchStatus: 'none',
      };

      const entity = await entityStore.create(entityData);
      await linkEntityToInvestigation(investigationId, entity.id);
      result.extracted++;

      // Progressive disclosure — emit immediately so UI can render
      eventBus.emit('investigation:entity-extracted', {
        investigationId,
        entity: entity as Entity,
      });

      await addLogEntry({
        investigationId,
        state: 'extract',
        action: 'entity_extracted',
        detail: {
          entityId: entity.id,
          entityName: entity.name,
          entityType: entity.type,
          taskId: task.id,
          sourceId: task.sourceId,
          confidence: extracted.provenance.confidence,
        },
      });
    }

    // Log rejections
    for (const rejected of extractionResult.rejected) {
      result.rejected++;
      await addLogEntry({
        investigationId,
        state: 'extract',
        action: 'entity_rejected',
        detail: {
          entity: rejected.entity,
          reason: rejected.reason,
          taskId: task.id,
          sourceId: task.sourceId,
        },
      });
    }

    // Track info gain for diminishing returns detection
    // Use per-task new entities (not cumulative result.extracted)
    const newEntitiesThisTask = extractionResult.extracted.length;
    const totalInTask = newEntitiesThisTask + extractionResult.rejected.length;
    const infoGain = totalInTask > 0 ? newEntitiesThisTask / totalInTask : 0;
    resourceManager.recordInfoGain(infoGain);

    // Emit resource update after each task extraction
    eventBus.emit('investigation:resource-update', {
      investigationId,
      resource: resourceManager.getState(),
    });

  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await addLogEntry({
      investigationId,
      state: 'extract',
      action: 'error',
      detail: {
        taskId: task.id,
        sourceId: task.sourceId,
        error: message,
      },
    });
  }

  return result;
}
